import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { Target, Brain, Lightbulb, Users, MessageSquare, HelpCircle } from 'lucide-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useClickOutside } from '@/hooks/useClickOutside';

export function SelfAssessmentView() {
  const { currentUser } = useAuth();
  const [summaryData, setSummaryData] = useState<{ paragraphs: string[] } | null>(null);
  const [discDetailData, setDiscDetailData] = useState<{
    profileType: string;
    strengths: string;
    challenges: string;
    preferredStyle: string;
    traits: Array<{
      title: string;
      description: string[];
    }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipContent = {
    'Behavioral Tendencies Under Pressure': 'Learn how you respond to stress and high-pressure scenarios. This trait highlights your ability to leverage strengths while addressing potential over-assertiveness. Balance is key to maintaining effective team dynamics during challenging situations.',
    'Blind Spots and Biases': 'Recognize unconscious behaviors or assumptions that might impact collaboration or decision-making. By addressing these blind spots, you align your intentions with your actions, fostering trust and better outcomes.',
    'Emotional Triggers and Responses': 'Understand how praise, criticism, and high-stakes environments affect your emotional responses. By identifying triggers, you can enhance emotional regulation and maintain composure in diverse situations.',
    'Decision-Making Style': 'Analyze whether your decisions are more logic-driven or collaborative. This trait emphasizes finding a balance between efficiency and inclusivity, ensuring sustainable outcomes that align with team goals.',
    'Feedback Responsiveness': 'Discover your openness to receiving and implementing feedback. This trait underscores the importance of active listening and iterative improvements to foster trust and professional growth.',
    'Self-Confidence vs. Self-Awareness': 'Explore how your confidence in certain skills aligns with external perceptions. This trait emphasizes balancing confidence with self-awareness to maintain humility and foster growth.',
    'Conflict Management Style': 'Understand your approach to resolving disagreements, whether assertive or collaborative. This trait highlights the value of empathy and active listening in achieving constructive conflict resolution.',
    'Alignment Between Intentions and Actions': 'Evaluate how well your intentions, such as fostering collaboration or driving results, are reflected in your behaviors. Consistency between values and actions builds credibility and impact.',
    'Growth Mindset Indicators': 'Reflect on your openness to learning and improvement. This trait identifies opportunities to strengthen your persistence and leverage challenges as pathways to growth.',
    'Influence and Impact': 'Assess how your charisma and communication style shape your influence. This trait helps you focus on empowering others and amplifying your leadership impact through shared vision.',
    'Personal Energy Management': 'Review how effectively you manage your energy and workload. This trait identifies risks of burnout and offers strategies for maintaining long-term productivity and work-life balance.',
    'Strategic Thinking vs. Execution Bias': 'Balance your focus on immediate tasks with long-term strategic planning. This trait emphasizes the importance of setting time aside for big-picture thinking to align day-to-day efforts with overarching goals.'
  } as const;
  const [stressData, setStressData] = useState<{
    stressLevel: number;
    reflection: string;
    resilienceTraits: string[];
    actionableInsights: string[];
  } | null>(null);
  const [discScores, setDiscScores] = useState<{
    dominance: number;
    influence: number;
    steadiness: number;
    conscientiousness: number;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useClickOutside(tooltipRef, () => {
    setShowTooltip(false);
  }, buttonRef);

  useEffect(() => {
    async function loadStressData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.self_assessment_stress) {
            setStressData(JSON.parse(data.self_assessment_stress));
          }
        }
      } catch (error) {
        console.error('Error loading stress data:', error);
      }
    }

    loadStressData();
  }, [currentUser]);

  useEffect(() => {
    async function loadDiscScores() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.self_assessment_disc_score) {
            setDiscScores(JSON.parse(data.self_assessment_disc_score));
          }
        }
      } catch (error) {
        console.error('Error loading DISC scores:', error);
      }
    }

    loadDiscScores();
  }, [currentUser]);

  useEffect(() => {
    async function loadSummaryData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);
        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.self_assessment_summary) {
            setSummaryData(JSON.parse(data.self_assessment_summary));
          }
          
          if (data.self_assessment_disc_detail) {
            try {
              const discData = JSON.parse(data.self_assessment_disc_detail);
              setDiscDetailData(discData);
            } catch (error) {
              console.error('Error parsing DISC detail data:', error);
              setDiscDetailData(null);
            }
          }
        }
      } catch (error) {
        console.error('Error loading summary data:', error);
        setError('Failed to load self-assessment summary');
      } finally {
        setLoading(false);
      }
    }

    loadSummaryData();
  }, [currentUser]);

  // DISC Profile data
  const discData = {
    labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
    datasets: discScores ? [{
        label: 'DISC Profile Assessment',
        data: [
          discScores.dominance,
          discScores.influence,
          discScores.steadiness,
          discScores.conscientiousness
        ],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)'
        ],
        borderColor: [
          'rgb(255, 99, 132)',
          'rgb(255, 206, 86)',
          'rgb(75, 192, 192)',
          'rgb(153, 102, 255)'
        ],
        borderWidth: 1,
        borderRadius: 5
      }] : []
  };

  const discOptions = {
    responsive: true,
    indexAxis: 'y' as const,
    scales: {
      x: {
        beginAtZero: true,
        max: 5,
        grid: {
          display: false
        }
      },
      y: {
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: false
      }
    }
  };

  const stressBarOptions = {
    responsive: true,
    indexAxis: 'y' as const,
    scales: {
      x: {
        min: 0,
        max: 5,
        grid: {
          display: false
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 12
          }
        }
      },
      y: {
        display: false
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        callbacks: {
          label: function(context: any) {
            if (context.dataIndex === 1) return null;
            return [
              `Current Level: ${context.raw}/5`,
              context.raw <= 3.5 ? 'Healthy Range' : context.raw <= 4.5 ? 'Moderate Range' : 'High Range'
            ];
          }
        }
      },
      legend: {
        display: false
      },
      maintainAspectRatio: false
    }
  };

  // Initialize carousel position
  useEffect(() => {
    const container = document.getElementById('insights-carousel');
    if (container && discDetailData?.traits?.length) {
      const cards = Array.from(document.querySelectorAll('.carousel-card'));

      const updateActiveCard = () => {
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        let closestIndex = 0;
        let minDistance = Infinity;

        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const cardCenter = cardRect.left + cardRect.width / 2;
          const distance = Math.abs(containerCenter - cardCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        setActiveCardIndex(closestIndex);
      };

      // Debounced scroll handler
      let scrollTimeout: NodeJS.Timeout;
      const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          requestAnimationFrame(updateActiveCard);
        }, 50);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });

      // Add a small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const firstCard = cards[0];
        if (firstCard && container) {
          const containerWidth = container.offsetWidth;
          const cardWidth = firstCard.offsetWidth;
          const scrollPosition = (firstCard.offsetLeft + cardWidth / 2) - (containerWidth / 2);
          
          // Set initial scroll position and active card
          container.scrollTo({ left: scrollPosition });
          updateActiveCard();
        }
      }, 50);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      };
    }
  }, [discDetailData?.traits]);

  return (
    <div className="bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6"
      >
        {/* Self-assessment Summary */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Self-assessment Summary</h2>
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg">
              {error}
            </div>
          ) : !summaryData ? (
            <div className="bg-gray-50 p-4 rounded-lg text-gray-600">
              Self-assessment summary will be available once your report is generated.
            </div>
          ) : (
            <div className="space-y-4">
              {summaryData.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-gray-600">{paragraph}</p>
              ))}
            </div>
          )}
        </div>

        {/* Mental Health Check */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Mental Health Check</h2>
          
          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              Evaluate your stress levels and resilience to understand how they impact your performance and well-being. Identify areas where you can maintain balance and thrive under pressure.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>This section provides a snapshot of your mental health, focusing on stress management and resilience:</p>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Stress Level Analysis</h4>
                  <p>Assess how external stressors affect your mood and communication style. Learn whether you fall within a healthy, moderate, or high-stress range.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Resilience Traits</h4>
                  <p>Uncover your ability to adapt and recover in challenging situations, pinpointing strengths and areas for improvement.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Actionable Insights</h4>
                  <p>Explore strategies to balance personal and professional life, ensuring sustainable performance and healthy team dynamics.</p>
                </div>
                
                <p>Regular check-ins with your mental health help you identify stress patterns and take proactive steps to improve emotional well-being and workplace relationships.</p>
              </div>
            </motion.div>
            
            <button
              onClick={() => setShowMore(!showMore)}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{showMore ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: showMore ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resilience Traits</h3>
              <ul className="space-y-3">
                {stressData?.resilienceTraits.map((trait, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="text-primary mt-1">•</span>
                    <span className="text-gray-600">{trait}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actionable Insights</h3>
              <ul className="space-y-3">
                {stressData?.actionableInsights.map((insight, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="text-secondary mt-1">•</span>
                    <span className="text-gray-600">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {!stressData ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Stress Analysis Not Available
              </h3>
              <p className="text-gray-600 max-w-lg mx-auto">
                The stress analysis will be available once your report is generated.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Stress Level</h3>
                <div className="max-w-lg mx-auto">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-lg font-semibold ${
                      stressData.stressLevel <= 3.5 
                        ? 'text-green-600' 
                        : stressData.stressLevel <= 4.5
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      {stressData.stressLevel.toFixed(1)}/5
                    </div>
                    <div className="text-sm font-medium text-gray-500">
                      {stressData.stressLevel <= 3.5 
                        ? 'Healthy Range' 
                        : stressData.stressLevel <= 4.5
                        ? 'Moderate Range'
                        : 'High Range'}
                    </div>
                  </div>
                  <div className="h-[60px]">
                    <Bar 
                      data={{
                        labels: ['Stress Level'],
                        datasets: [{
                          data: [stressData.stressLevel],
                          backgroundColor: (context: any) => {
                            const value = context.raw;
                            if (value <= 3.5) return 'rgba(34, 197, 94, 0.15)';
                            if (value <= 4.5) return 'rgba(234, 179, 8, 0.15)';
                            return 'rgba(239, 68, 68, 0.15)';
                          },
                          borderColor: (context: any) => {
                            const value = context.raw;
                            if (value <= 3.5) return 'rgb(34, 197, 94)';
                            if (value <= 4.5) return 'rgb(234, 179, 8)';
                            return 'rgb(239, 68, 68)';
                          },
                          borderWidth: 1,
                          borderRadius: 4,
                          barThickness: 12
                        }]
                      }} 
                      options={stressBarOptions} 
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>Low</span>
                    <span>Moderate</span>
                    <span>High</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-600">{stressData.reflection}</p>
              </div>
            </>
          )}
        </div>

        {/* DISC Results */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">DISC Results</h2>

          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              Discover how your personality traits—Dominance, Influence, Steadiness, and Conscientiousness—shape your behavior, communication, and leadership style.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>The DISC assessment evaluates your personality through four key traits:</p>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Dominance</h4>
                  <p>Your drive to tackle challenges and take control in high-stakes situations.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Influence</h4>
                  <p>How effectively you engage and inspire others through communication and relationships.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Steadiness</h4>
                  <p>Your consistency, patience, and ability to maintain harmony in teams.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Conscientiousness</h4>
                  <p>Your attention to detail and adherence to rules and standards.</p>
                </div>

                <p>By understanding your DISC profile, you can:</p>
                
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Leverage Strengths: Identify natural advantages, such as being results-driven, collaborative, or detail-oriented.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Recognize Challenges: Address areas like over-assertiveness, hesitancy in decision-making, or difficulty adapting to rapid changes.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Improve Collaboration: Adapt your style to work more effectively with others based on their DISC traits.</span>
                  </li>
                </ul>
                
                <p>This assessment empowers you to enhance interpersonal relationships, lead with intention, and align your behaviors with team and organizational goals.</p>
              </div>
            </motion.div>
            
            <button
              onClick={() => setShowMore(!showMore)}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{showMore ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: showMore ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* DISC Style Profile */}
            <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                <div className="mb-2">
                  {discDetailData ? (
                    <>
                      <h3 className="text-xl font-bold text-gray-900">DISC Style Profile:</h3>
                      <p className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mt-2">
                        {discDetailData.profileType}
                      </p>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">DISC profile data will be available once your report is generated.</p>
                    </div>
                  )}
                </div>
                {discDetailData && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                        Strengths
                      </h4>
                      <p className="text-gray-600 pl-4">{discDetailData.strengths}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                        Challenges
                      </h4>
                      <p className="text-gray-600 pl-4">{discDetailData.challenges}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-[#f9c55f] rounded-full mr-2"></span>
                        Preferred Style
                      </h4>
                      <p className="text-gray-600 pl-4">{discDetailData.preferredStyle}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DISC Profile Assessment */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2 mb-4">
                <Target className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-semibold text-gray-900">DISC Profile Assessment</h3>
              </div>
              {!discScores ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">DISC assessment data will be available once your report is generated.</p>
                  </div>
                </div>
              ) : (
                <div className="relative h-[400px]">
                  <Bar data={discData} options={discOptions} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Insights Cards */}
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Profile Description and Insights</h2>
          </div>

          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed">
              Enhance your self-awareness by reflecting on these behavioral patterns derived from your DISC profile. While these insights are designed to encourage self-reflection, remember <strong>they are not absolute truths or fixed traits</strong>. Your behavior may shift depending on the context and moment, so use these affirmations as a guide, not a definitive assessment.
            </p>
          </div>
          
          {/* Card Position Indicator */}
          <div className="text-center mb-4">
            <span className="inline-block px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
              {activeCardIndex + 1}/12
            </span>
          </div>
          
          <div className="relative">
            {/* Navigation Buttons */}
            <button 
              onClick={() => {
                const container = document.getElementById('insights-carousel');
                if (container) {
                  container.scrollBy({ left: -320, behavior: 'smooth' });
                }
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button 
              onClick={() => {
                const container = document.getElementById('insights-carousel');
                if (container) {
                  container.scrollBy({ left: 320, behavior: 'smooth' });
                }
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent z-[1] pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent z-[1] pointer-events-none" />

            {/* Cards Container */}
            <div 
              id="insights-carousel"
              className="flex overflow-x-auto gap-6 pb-4 snap-x snap-mandatory hide-scrollbar relative min-h-[300px]"
              style={{ 
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                paddingLeft: '1rem',
                paddingRight: '1rem'
              }}
            >
              {/* Initial spacer for proper centering */}
              <div className="flex-none w-[calc(50%-150px)]" aria-hidden="true" />

              {/* Cards */}
              {discDetailData ? (
                discDetailData.traits.map((trait, index) => (
                  <div
                    key={index}
                    className={`flex-none w-[300px] bg-white rounded-xl shadow-sm p-6 snap-center carousel-card transform transition-all duration-300 ease-out ${
                      activeCardIndex === index ? 'scale-[1.02] shadow-lg border-2 border-primary' : 'scale-100 border border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="relative flex items-center justify-center mb-3">
                      <h4 className="font-semibold text-gray-900 text-center">{trait.title}</h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowTooltip(showTooltip === `card${index}` ? null : `card${index}`);
                        }}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      {showTooltip === `card${index}` && (
                        <div
                          ref={tooltipRef}
                          className="absolute top-full mt-2 p-4 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-64"
                        >
                          <p className="text-sm text-gray-600">
                            {tooltipContent[trait.title as keyof typeof tooltipContent]}
                          </p>
                        </div>
                      )}
                    </div>
                    <ul className="space-y-2 text-gray-600 text-sm">
                      {trait.description.map((desc, i) => (
                        <li key={i}>{desc}</li>
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <div className="flex-none w-[300px] bg-white rounded-xl shadow-sm p-6 snap-center carousel-card">
                  <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">DISC trait analysis will be available once your report is generated.</p>
                  </div>
                </div>
              )}

              {/* Final spacer for proper centering */}
              <div className="flex-none w-[calc(50%-150px)]" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Rest of the component remains unchanged */}
      </motion.div>
    </div>
  );
}